import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Loader, Settings, Globe, Mail, Phone, MapPin, BarChart2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';
import { useRefreshSettings } from '@/context/SiteSettingsContext';

const API = '/api';
function getHeaders() { const t = localStorage.getItem('pkepl_token'); return t ? { Authorization: `Bearer ${t}` } : {}; }

const TABS = [
  { key: 'general', label: 'General', icon: Settings },
  { key: 'branding', label: 'Branding & Logo', icon: Globe },
  { key: 'contact', label: 'Contact Info', icon: Mail },
  { key: 'seo', label: 'SEO & Meta', icon: BarChart2 },
  { key: 'social', label: 'Social & Footer', icon: Share2 },
];

const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-semibold text-[#374151] font-sans mb-4 mt-1">{children}</h3>
);

const Divider = () => <div className="border-t border-[#E5E7EB] pt-5" />;

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <label className="block text-xs font-semibold text-[#374151] font-sans uppercase tracking-wide">{label}</label>
    {children}
    {hint && <p className="text-xs text-[#9CA3AF] font-sans mt-1">{hint}</p>}
  </div>
);

const Input = (props) => (
  <input {...props} className="w-full px-3.5 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0E5A57]/20 focus:border-[#0E5A57] font-sans bg-white text-[#1F2937] placeholder-[#9CA3AF]" />
);

const Textarea = (props) => (
  <textarea {...props} className="w-full px-3.5 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0E5A57]/20 focus:border-[#0E5A57] font-sans bg-white text-[#1F2937] placeholder-[#9CA3AF] resize-none" />
);

const Select = ({ value, onChange, children }) => (
  <select value={value} onChange={onChange} className="w-full px-3.5 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0E5A57]/20 focus:border-[#0E5A57] font-sans bg-white text-[#1F2937]">
    {children}
  </select>
);

export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const refreshSettings = useRefreshSettings();

  useEffect(() => {
    axios.get(`${API}/admin/settings`, { withCredentials: true, headers: getHeaders() })
      .then(r => setSettings(r.data)).catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const set = (key, value) => setSettings(s => ({ ...s, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/settings`, settings, { withCredentials: true, headers: getHeaders() });
      setSettings(data);
      refreshSettings();
      toast.success('Settings saved successfully');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader className="w-6 h-6 animate-spin text-[#0E5A57]" /></div>;

  const logoUrl = (idKey, urlField) => settings[urlField] || (settings[idKey] ? `/api/media/${settings[idKey]}` : null);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-[#153E5C]">Website Settings</h1>
          <p className="text-sm text-[#6B7280] font-sans mt-1">Manage global site configuration, branding, SEO, and social links.</p>
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="settings-save-btn"
          className="flex items-center gap-2 bg-[#0E5A57] hover:bg-[#2E7D7A] disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors">
          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#E5E7EB] flex gap-0 overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === key ? 'border-[#0E5A57] text-[#0E5A57]' : 'border-transparent text-[#6B7280] hover:text-[#374151]'}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 space-y-6">

        {/* General */}
        {activeTab === 'general' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Brand Name (Public-facing)">
                <Input value={settings.brand_name || ''} onChange={e => set('brand_name', e.target.value)} placeholder="NKP VENTURES" data-testid="settings-brand-name" />
              </Field>
              <Field label="Company Legal Name">
                <Input value={settings.company_name || ''} onChange={e => set('company_name', e.target.value)} placeholder="PKRUPARTH ENTERPRISE PVT. LTD." data-testid="settings-company-name" />
              </Field>
            </div>
            <Field label="Tagline" hint="Shown in the footer and meta description defaults.">
              <Input value={settings.tagline || ''} onChange={e => set('tagline', e.target.value)} placeholder="Empowering Businesses. Delivering Excellence." data-testid="settings-tagline" />
            </Field>
            <Field label="Business Hours">
              <Input value={settings.business_hours || ''} onChange={e => set('business_hours', e.target.value)} placeholder="Monday – Friday: 09:00 – 18:00 IST" />
            </Field>
            <Field label="Admin Notification Email" hint="Contact form submissions will be sent to this address.">
              <Input type="email" value={settings.notification_email || ''} onChange={e => set('notification_email', e.target.value)} placeholder="admin@yourcompany.com" />
            </Field>
          </>
        )}

        {/* Branding */}
        {activeTab === 'branding' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <ImageUpload
                label="Primary Logo"
                value={settings.primary_logo_id}
                previewUrl={logoUrl('primary_logo_id', 'primary_logo_url')}
                onChange={(id, url) => setSettings(s => ({ ...s, primary_logo_id: id, primary_logo_url: url }))}
                onClear={() => setSettings(s => ({ ...s, primary_logo_id: null, primary_logo_url: null }))}
                hint="Used in navigation on light backgrounds. JPG, PNG, SVG · Max 5MB"
              />
              <ImageUpload
                label="Light Logo (For Dark Backgrounds)"
                value={settings.light_logo_id}
                previewUrl={logoUrl('light_logo_id', 'light_logo_url')}
                onChange={(id, url) => setSettings(s => ({ ...s, light_logo_id: id, light_logo_url: url }))}
                onClear={() => setSettings(s => ({ ...s, light_logo_id: null, light_logo_url: null }))}
                hint="Used in navigation on dark/hero backgrounds. White/light version."
              />
              <ImageUpload
                label="Dark Logo (For Light Backgrounds)"
                value={settings.dark_logo_id}
                previewUrl={logoUrl('dark_logo_id', 'dark_logo_url')}
                onChange={(id, url) => setSettings(s => ({ ...s, dark_logo_id: id, dark_logo_url: url }))}
                onClear={() => setSettings(s => ({ ...s, dark_logo_id: null, dark_logo_url: null }))}
                hint="Alternative dark version for use on light backgrounds."
              />
              <ImageUpload
                label="Favicon"
                value={settings.favicon_id}
                previewUrl={logoUrl('favicon_id', 'favicon_url')}
                onChange={(id, url) => setSettings(s => ({ ...s, favicon_id: id, favicon_url: url }))}
                onClear={() => setSettings(s => ({ ...s, favicon_id: null, favicon_url: null }))}
                hint="Browser tab icon. PNG or SVG, ideally 32×32 or 64×64."
              />
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
              <p className="text-sm text-amber-700 font-sans">
                <strong>Logo usage:</strong> If no logo is uploaded, the text wordmark &quot;NKP VENTURES&quot; is displayed. Uploading a logo will replace the text wordmark automatically in both the header and footer.
              </p>
            </div>
          </div>
        )}

        {/* Contact */}
        {activeTab === 'contact' && (
          <div className="space-y-5">
            <Field label="Email Address">
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 w-4 h-4 text-[#9CA3AF]" />
                <Input type="email" value={settings.email || ''} onChange={e => set('email', e.target.value)} placeholder="contact@company.com" style={{ paddingLeft: '2.25rem' }} data-testid="settings-email" />
              </div>
            </Field>
            <Field label="Phone Number">
              <div className="relative">
                <Phone className="absolute left-3 top-2.5 w-4 h-4 text-[#9CA3AF]" />
                <Input value={settings.phone || ''} onChange={e => set('phone', e.target.value)} placeholder="+91 99251 90747" style={{ paddingLeft: '2.25rem' }} data-testid="settings-phone" />
              </div>
            </Field>
            <Field label="Address">
              <div className="relative">
                <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-[#9CA3AF]" />
                <Textarea value={settings.address || ''} onChange={e => set('address', e.target.value)} rows={3} placeholder="Full business address" style={{ paddingLeft: '2.25rem' }} data-testid="settings-address" />
              </div>
            </Field>
            <Field label="Business Hours">
              <Input value={settings.business_hours || ''} onChange={e => set('business_hours', e.target.value)} placeholder="Monday – Friday: 09:00 – 18:00 IST" />
            </Field>
          </div>
        )}

        {/* SEO */}
        {activeTab === 'seo' && (
          <div className="space-y-6">

            {/* Default Meta */}
            <div className="space-y-4">
              <SectionTitle>Default Meta Tags</SectionTitle>
              <Field label="Default Meta Title" hint="Recommended: 50–60 characters. Used when a page has no specific title.">
                <Input value={settings.seo_title_default || ''} onChange={e => set('seo_title_default', e.target.value)} placeholder="NKP VENTURES — Premium BPO Partner" data-testid="settings-seo-title" />
              </Field>
              <Field label="Default Meta Description" hint="Recommended: 150–160 characters.">
                <Textarea value={settings.seo_description_default || ''} onChange={e => set('seo_description_default', e.target.value)} rows={3} placeholder="NKP VENTURES provides premium BPO services..." data-testid="settings-seo-description" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Meta Keywords" hint="Comma-separated keywords.">
                  <Input value={settings.meta_keywords || ''} onChange={e => set('meta_keywords', e.target.value)} placeholder="BPO, outsourcing, administrative support" />
                </Field>
                <Field label="Robots">
                  <Select value={settings.robots_setting || 'index, follow, max-snippet:-1, max-image-preview:large'} onChange={e => set('robots_setting', e.target.value)}>
                    <option value="index, follow, max-snippet:-1, max-image-preview:large">index, follow (recommended)</option>
                    <option value="index, follow">index, follow</option>
                    <option value="noindex, follow">noindex, follow</option>
                    <option value="noindex, nofollow">noindex, nofollow</option>
                  </Select>
                </Field>
              </div>
              <Field label="Default Canonical URL" hint="Leave blank to use the current page URL automatically.">
                <Input value={settings.canonical_url || ''} onChange={e => set('canonical_url', e.target.value)} placeholder="https://yourwebsite.com" />
              </Field>
            </div>

            {/* Open Graph */}
            <Divider />
            <div className="space-y-4">
              <SectionTitle>Open Graph (Facebook / LinkedIn)</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="OG Title" hint="Leave blank to use Default Meta Title.">
                  <Input value={settings.og_title || ''} onChange={e => set('og_title', e.target.value)} placeholder="NKP VENTURES — Your BPO Partner" />
                </Field>
                <Field label="OG Image URL" hint="Recommended: 1200×630px. Use absolute URL.">
                  <Input value={settings.og_image || ''} onChange={e => set('og_image', e.target.value)} placeholder="https://yoursite.com/og-image.jpg" />
                </Field>
              </div>
              <Field label="OG Description" hint="Leave blank to use Default Meta Description.">
                <Textarea value={settings.og_description || ''} onChange={e => set('og_description', e.target.value)} rows={2} placeholder="NKP VENTURES provides premium BPO services..." />
              </Field>
            </div>

            {/* Twitter Card */}
            <Divider />
            <div className="space-y-4">
              <SectionTitle>Twitter / X Card</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Card Type">
                  <Select value={settings.twitter_card || 'summary_large_image'} onChange={e => set('twitter_card', e.target.value)}>
                    <option value="summary_large_image">summary_large_image (recommended)</option>
                    <option value="summary">summary</option>
                  </Select>
                </Field>
                <Field label="Twitter Image URL" hint="Leave blank to use OG Image.">
                  <Input value={settings.twitter_image || ''} onChange={e => set('twitter_image', e.target.value)} placeholder="https://yoursite.com/twitter-image.jpg" />
                </Field>
                <Field label="Twitter Title" hint="Leave blank to use Default Meta Title.">
                  <Input value={settings.twitter_title || ''} onChange={e => set('twitter_title', e.target.value)} placeholder="NKP VENTURES" />
                </Field>
                <Field label="Twitter Description" hint="Leave blank to use Default Meta Description.">
                  <Input value={settings.twitter_description || ''} onChange={e => set('twitter_description', e.target.value)} placeholder="Premium BPO services for international businesses." />
                </Field>
              </div>
            </div>

            {/* Site Verification */}
            <Divider />
            <div className="space-y-4">
              <SectionTitle>Site Verification</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Google Verification">
                  <Input value={settings.google_verification || ''} onChange={e => set('google_verification', e.target.value)} placeholder="google-site-verification code" />
                </Field>
                <Field label="Bing Verification">
                  <Input value={settings.bing_verification || ''} onChange={e => set('bing_verification', e.target.value)} placeholder="msvalidate.01 code" />
                </Field>
                <Field label="Facebook Verification">
                  <Input value={settings.facebook_verification || ''} onChange={e => set('facebook_verification', e.target.value)} placeholder="facebook-domain-verification code" />
                </Field>
              </div>
            </div>

            {/* Analytics */}
            <Divider />
            <div className="space-y-4">
              <SectionTitle>Analytics & Tracking</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Google Analytics ID">
                  <Input value={settings.ga_id || ''} onChange={e => set('ga_id', e.target.value)} placeholder="G-XXXXXXXXXX" />
                </Field>
                <Field label="Google Tag Manager ID">
                  <Input value={settings.gtm_id || ''} onChange={e => set('gtm_id', e.target.value)} placeholder="GTM-XXXXXXX" />
                </Field>
                <Field label="Meta Pixel ID">
                  <Input value={settings.meta_pixel_id || ''} onChange={e => set('meta_pixel_id', e.target.value)} placeholder="000000000000000" />
                </Field>
              </div>
            </div>

            {/* Structured Data */}
            <Divider />
            <div className="space-y-4">
              <SectionTitle>Custom Structured Data (JSON-LD)</SectionTitle>
              <Field label="JSON-LD Override" hint="Enter a valid JSON-LD object. This overrides the default Organization schema. Leave blank to use the auto-generated schema.">
                <Textarea
                  value={settings.structured_data_json || ''}
                  onChange={e => set('structured_data_json', e.target.value)}
                  rows={6}
                  placeholder={'{\n  "@context": "https://schema.org",\n  "@type": "Organization",\n  "name": "NKP VENTURES"\n}'}
                  className="font-mono text-xs"
                />
              </Field>
            </div>

          </div>
        )}

        {/* Social & Footer */}
        {activeTab === 'social' && (
          <div className="space-y-6">
            <div className="space-y-4">
              <SectionTitle>Social Media Links</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="LinkedIn URL">
                  <Input value={settings.social_linkedin || ''} onChange={e => set('social_linkedin', e.target.value)} placeholder="https://linkedin.com/company/..." data-testid="settings-linkedin" />
                </Field>
                <Field label="Twitter / X URL">
                  <Input value={settings.social_twitter || ''} onChange={e => set('social_twitter', e.target.value)} placeholder="https://twitter.com/..." />
                </Field>
                <Field label="Facebook URL">
                  <Input value={settings.social_facebook || ''} onChange={e => set('social_facebook', e.target.value)} placeholder="https://facebook.com/..." />
                </Field>
                <Field label="Instagram URL">
                  <Input value={settings.social_instagram || ''} onChange={e => set('social_instagram', e.target.value)} placeholder="https://instagram.com/..." />
                </Field>
                <Field label="YouTube URL">
                  <Input value={settings.social_youtube || ''} onChange={e => set('social_youtube', e.target.value)} placeholder="https://youtube.com/c/..." data-testid="settings-youtube" />
                </Field>
                <Field label="WhatsApp URL" hint="Format: https://wa.me/[country code][number], e.g. https://wa.me/919925190747">
                  <Input value={settings.social_whatsapp || ''} onChange={e => set('social_whatsapp', e.target.value)} placeholder="https://wa.me/919925190747" data-testid="settings-whatsapp" />
                </Field>
              </div>
            </div>

            <Divider />
            <div className="space-y-4">
              <SectionTitle>Footer Content</SectionTitle>
              <Field label="Footer Tagline" hint="Displayed below the logo in the footer.">
                <Input value={settings.footer_tagline || ''} onChange={e => set('footer_tagline', e.target.value)} placeholder="Empowering Businesses. Delivering Excellence." data-testid="settings-footer-tagline" />
              </Field>
              <Field label="Footer Description" hint="Optional additional description below the tagline.">
                <Textarea value={settings.footer_description || ''} onChange={e => set('footer_description', e.target.value)} rows={2} placeholder="Short company description for footer..." />
              </Field>
              <Field label="Copyright Text" hint={`Use {year} for the current year.`}>
                <Input value={settings.copyright_text || ''} onChange={e => set('copyright_text', e.target.value)} placeholder="© {year} PKRUPARTH ENTERPRISE PVT. LTD. All rights reserved." data-testid="settings-copyright" />
              </Field>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
